import { HttpClient } from '@angular/common/http';
import { Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import * as SpaceNamePatch from '../../../server/routes/spaces/name/patch';
import * as SpaceUserPost from '../../../server/routes/spaces/users/post';
import {
  generatePath,
  orgNameMaxLength,
} from '../../../server/services/utils.service';
import type { Org, OrgSpace, OrgUser } from '../../../server/types/org-types';
import type { DeepReadonly } from '../../../server/util-type';
import { AuthUserService } from '../auth-user.service';
import {
  SpaceRemoveDialogComponent,
  type SpaceRemoveDialogComponentData,
} from '../space-remove-dialog/space-remove-dialog.component';
import { TitleService } from '../title.service';

@Component({
  selector: 'app-space-settings',
  templateUrl: './space-settings.component.html',
  styleUrls: ['./space-settings.component.scss'],
  standalone: false,
})
export class SpaceSettingsComponent implements OnInit, OnDestroy {
  private http = inject(HttpClient);
  private snackBar = inject(MatSnackBar);
  private authUserService = inject(AuthUserService);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private titleService = inject(TitleService);

  org: DeepReadonly<Org>;
  // accountId: string;
  space: DeepReadonly<OrgSpace>;
  users: OrgUser[] = [];
  isAdmin = false;

  name: string;
  canEditName = false;
  nameControl: FormControl<string>;
  nameChanged = false;
  nameUpdateState: 'none' | 'waiting' | 'complete' = 'none';

  showAddUsers = false;
  filteredUsers: OrgUser[] = [];
  selectUserControl: FormControl<string>;
  possibleUsers = true;

  pageState: 'waiting' | 'ready' | 'error' = 'waiting';

  subscriptions = new Subscription();

  ngOnInit(): void {
    this.titleService.set('Space settings');

    // Search control
    this.selectUserControl = new FormControl('', { nonNullable: true });
    const searchSub = this.selectUserControl.valueChanges.subscribe((value) => {
      this.updateFilteredUsers(value);
    });
    this.subscriptions.add(searchSub);

    const sub = this.authUserService.userOrgObj.subscribe((userOrgObj) => {
      if (!userOrgObj) return; // This should not happen except at logout

      this.org = userOrgObj.org;
      this.isAdmin = userOrgObj.user.admin;

      const spaceId: string = this.route.snapshot.params.spaceId;

      const space = this.org.spaces.find((s) => s._id === spaceId);
      if (!space) {
        this.pageState = 'error';
        return;
      }
      this.space = space;

      // Reset users and refill if there is a change
      this.users = [];
      space.users.forEach((spaceUser) => {
        const user = this.org.users.find(
          (orgUser) => orgUser._id === spaceUser._id
        );
        if (user) this.users.push(user);
      });

      // Name control
      this.name = this.space.name;
      this.canEditName = userOrgObj.user.admin;
      if (this.nameControl) {
        this.nameControl.setValue(this.name);
      } else {
        this.nameControl = new FormControl(this.name, {
          nonNullable: true,
          validators: [
            Validators.required,
            Validators.maxLength(orgNameMaxLength),
          ],
        });
        const sub1 = this.nameControl.valueChanges.subscribe((value) => {
          this.nameChanged = value !== this.name;
          if (this.nameChanged && this.nameUpdateState === 'complete') {
            this.nameUpdateState = 'none';
          }
        });
        this.subscriptions.add(sub1);
      }

      // Initialize search control
      if (!this.selectUserControl.value) this.updateFilteredUsers('');

      this.pageState = 'ready';
    });
    this.subscriptions.add(sub);
  }

  saveName() {
    const uncleanName = this.nameControl.value;
    const name = uncleanName.trim();
    const path = generatePath<SpaceNamePatch.ReqParams>(
      '/api/orgs/:orgId/spaces/:spaceId/name',
      { orgId: this.org._id, spaceId: this.space._id }
    );
    this.nameChanged = false;
    const body: SpaceNamePatch.ReqBody = { name };
    this.nameUpdateState = 'waiting';
    const sub = this.http.patch<SpaceNamePatch.ResBody>(path, body).subscribe({
      next: ({ org }) => {
        this.nameUpdateState = 'complete';
        this.authUserService.updateOrg({ org });
      },
      error: (err) => {
        this.nameUpdateState = 'none';
        this.snackBar.open('ERROR: could not update space name.', 'Dismiss', {
          duration: 5000,
        });
      },
    });
    this.subscriptions.add(sub);
  }

  removeUser(userId: string) {
    const dialogConfig = new MatDialogConfig();

    const user = this.users.find((u) => u._id === userId);

    if (!user) throw new Error('user not found when removing user from space');
    const { org, space } = this;
    const data: SpaceRemoveDialogComponentData = { org, user, space };
    dialogConfig.data = data;
    this.dialog.open(SpaceRemoveDialogComponent, dialogConfig);
  }

  selected(userId: string) {
    // Reset text immediately
    this.selectUserControl.setValue('');

    const path = generatePath<SpaceUserPost.ReqParams>(
      '/api/orgs/:orgId/spaces/:spaceId/users',
      {
        orgId: this.org._id,
        spaceId: this.space._id,
      }
    );
    const body: SpaceUserPost.ReqBody = { userId };

    const sub = this.http.post<SpaceUserPost.ResBody>(path, body).subscribe({
      next: ({ org }) => {
        this.authUserService.updateOrg({ org });
      },
      error: (err) => {
        console.error(err);
        this.snackBar.open('ERROR: could not add user to space.', 'Dismiss', {
          duration: 5000,
        });
      },
    });
    this.subscriptions.add(sub);
  }

  updateFilteredUsers(value: string) {
    const matchingUsers = value
      ? this.authUserService.searchUsers({ orgId: this.org._id, value })
      : this.org.users;
    // Remove users that are already on the space and are archived
    const existingUserIds = this.users.map((u) => u._id);
    this.filteredUsers = matchingUsers.filter(
      (u) => !existingUserIds.includes(u._id) && !u.archivedAt
    );
    // Determine if there are more users that can be added
    this.possibleUsers = !!this.org.users.filter(
      (u) => !existingUserIds.includes(u._id)
    ).length;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
